import React, { FormEvent, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { timeProfilesAPI } from '../services/api';
import { CreateTimeProfile, TimeProfile } from '../types';
import TableLoadingRow from './TableLoadingRow';
import TimeSelect from './TimeSelect';

const defaultForm: CreateTimeProfile = {
  displayName: '',
  email: '',
  identityUserId: '',
  role: 'User',
  isActive: true,
  ignoreForWarnings: false,
  expectedWeeklyHours: 40,
};

const roleOptions = [
  { value: 'User', label: 'User' },
  { value: 'Admin', label: 'Admin' },
  { value: 'ReportViewer', label: 'Report Viewer' },
  { value: 'ProjectManager', label: 'Project Manager' },
];

type StatusFilter = 'all' | 'active' | 'inactive';

const statusFilterOptions: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const EmployeesPage: React.FC = () => {
  const [profiles, setProfiles] = useState<TimeProfile[]>([]);
  const [form, setForm] = useState<CreateTimeProfile>(defaultForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const visibleProfiles = profiles.filter(profile => {
    if (statusFilter === 'active') {
      return profile.isActive;
    }

    if (statusFilter === 'inactive') {
      return !profile.isActive;
    }

    return true;
  });

  const loadProfiles = async () => {
    setLoading(true);
    try {
      setProfiles(await timeProfilesAPI.getProfiles(statusFilter !== 'active'));
    } catch {
      toast.error('Members could not be loaded');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const resetForm = () => {
    setForm({ ...defaultForm });
    setEditingId(null);
    setFormOpen(false);
  };

  const openNewProfile = () => {
    setForm({ ...defaultForm });
    setEditingId(null);
    setFormOpen(true);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (saving) {
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await timeProfilesAPI.updateProfile(editingId, { ...form, id: editingId });
        toast.success('Member updated');
      } else {
        await timeProfilesAPI.createProfile(form);
        toast.success('Member created');
      }
      resetForm();
      await loadProfiles();
    } catch {
      toast.error('Member could not be saved');
    } finally {
      setSaving(false);
    }
  };

  const editProfile = (profile: TimeProfile) => {
    setEditingId(profile.id);
    setForm({
      displayName: profile.displayName,
      email: profile.email || '',
      identityUserId: profile.identityUserId || '',
      role: profile.role || 'User',
      personId: profile.personId,
      isActive: profile.isActive,
      ignoreForWarnings: profile.ignoreForWarnings ?? false,
      expectedWeeklyHours: profile.expectedWeeklyHours,
    });
    setFormOpen(true);
  };

  const deactivateProfile = async (id: number) => {
    try {
      await timeProfilesAPI.deleteProfile(id);
      toast.success('Member deactivated');
      await loadProfiles();
    } catch {
      toast.error('Member could not be deactivated');
    }
  };

  const employeeForm = (
    <form onSubmit={handleSubmit} className="time-form employee-form">
      <div className="form-group employee-form-wide-field">
        <label className="form-label" htmlFor="displayName">Name</label>
        <input
          id="displayName"
          className="form-control"
          value={form.displayName}
          onChange={(event) => setForm(prev => ({ ...prev, displayName: event.target.value }))}
          required
        />
      </div>
      <div className="form-group employee-form-wide-field">
        <label className="form-label" htmlFor="email">Email</label>
        <input
          id="email"
          className="form-control"
          type="email"
          value={form.email || ''}
          onChange={(event) => setForm(prev => ({ ...prev, email: event.target.value }))}
          required
        />
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="role">Role</label>
        <TimeSelect
          inputId="role"
          ariaLabel="Role"
          options={roleOptions}
          value={form.role}
          onChange={(value) => setForm(prev => ({ ...prev, role: value || 'User' }))}
          placeholder="Role"
        />
      </div>
      <div className="form-group">
        <label className="form-label" htmlFor="expectedWeeklyHours">Weekly hours</label>
        <input
          id="expectedWeeklyHours"
          className="form-control"
          type="number"
          min="0"
          max="168"
          step="0.25"
          value={form.expectedWeeklyHours}
          onChange={(event) => setForm(prev => ({ ...prev, expectedWeeklyHours: Number(event.target.value) }))}
        />
      </div>
      <label className="toggle-row">
        <input
          type="checkbox"
          checked={form.isActive}
          onChange={(event) => setForm(prev => ({ ...prev, isActive: event.target.checked }))}
        />
        Active
      </label>
      <label className="toggle-row">
        <input
          type="checkbox"
          checked={form.ignoreForWarnings}
          onChange={(event) => setForm(prev => ({ ...prev, ignoreForWarnings: event.target.checked }))}
        />
        Ignore for warnings
      </label>
      <div className="form-actions employee-form-wide-field">
        <button className="btn btn-primary" type="submit" disabled={saving} aria-busy={saving}>
          {saving ? (
            <span className="spinner-border spinner-border-sm" aria-hidden="true" />
          ) : (
            <i className="fa-solid fa-floppy-disk" aria-hidden="true" />
          )}
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button className="btn btn-secondary" type="button" onClick={resetForm}>
          Cancel
        </button>
      </div>
    </form>
  );

  return (
    <div className="fade-in">
      <div className="page-header">
        <div>
          <h1>Members</h1>
          <p>{visibleProfiles.length} profiles</p>
        </div>
        <div className="action-row">
          <div className="form-group member-status-filter">
            <label className="form-label" htmlFor="member-status-filter">Status</label>
            <TimeSelect
              inputId="member-status-filter"
              ariaLabel="Member status filter"
              options={statusFilterOptions}
              value={statusFilter}
              onChange={(value) => setStatusFilter((value || 'all') as StatusFilter)}
              placeholder="Status"
            />
          </div>
          <button className="btn btn-primary" type="button" onClick={openNewProfile}>
            <i className="fa-solid fa-plus" aria-hidden="true" />
            New member
          </button>
        </div>
      </div>

      {isFormOpen && (
        <div
          className="time-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              resetForm();
            }
          }}
        >
          <section className="time-modal employee-modal" role="dialog" aria-modal="true" aria-labelledby="employee-dialog-title">
            <div className="time-modal-header">
              <h5 id="employee-dialog-title" className="card-title">{editingId ? 'Edit member' : 'New member'}</h5>
              <button className="icon-button" type="button" onClick={resetForm} aria-label="Close member form">
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </div>
            <div className="time-modal-body">
              {employeeForm}
            </div>
          </section>
        </div>
      )}

      <div className="time-grid">

        <section className="card">
          <div className="card-header">
            <h5 className="card-title">Member list</h5>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Weekly</th>
                    <th>Status</th>
                    <th>Warnings</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <TableLoadingRow colSpan={7} label="Loading members..." />}
                  {!loading && visibleProfiles.map(profile => (
                    <tr key={profile.id} className={!profile.isActive ? 'inactive-member-row' : undefined}>
                      <td data-label="Name">{profile.displayName}</td>
                      <td data-label="Email">{profile.email || ''}</td>
                      <td data-label="Role">{roleOptions.find(option => option.value === profile.role)?.label || profile.role || ''}</td>
                      <td data-label="Weekly">{profile.expectedWeeklyHours}h</td>
                      <td data-label="Status">
                        <span className={`badge ${profile.isActive ? 'badge-success' : 'badge-warning'}`}>
                          {profile.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td data-label="Warnings">
                        <span className={`badge ${profile.ignoreForWarnings ? 'badge-secondary' : 'badge-success'}`}>
                          {profile.ignoreForWarnings ? 'Ignored' : 'Included'}
                        </span>
                      </td>
                      <td data-label="Actions">
                        <div className="action-row">
                          <button className="action-btn" type="button" onClick={() => editProfile(profile)} title="Edit" aria-label={`Edit ${profile.displayName}`}>
                            <i className="fa-solid fa-pen" aria-hidden="true" />
                          </button>
                          <button className="action-btn danger" type="button" onClick={() => deactivateProfile(profile.id)} title="Deactivate" aria-label={`Deactivate ${profile.displayName}`}>
                            <i className="fa-solid fa-user-slash" aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && visibleProfiles.length === 0 && (
                    <tr>
                      <td colSpan={7}><div className="empty-state">No members</div></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default EmployeesPage;
